import { useMemo, MouseEvent, memo } from "react";
import { useSortable } from "@dnd-kit/sortable";

import { AdminGetRoutesLocationsResDto } from "service/api/estabrookSchemas";
import { CSS } from "@dnd-kit/utilities";
import LocationAddressComponent from "../../region-management/location-address/location-address";
import { ClickedAddress } from "../../../../shared/hooks/use-address-click-handler";
import { RouteStatus } from "../helpers/route-status.enum";
import { getEffectiveServiceTime } from "../helpers/completed-stack-shifts";

interface OrderItemProps {
  order?: AdminGetRoutesLocationsResDto;
  routeId?: string;
  regionId?: string;
  color: string;
  selected: boolean;
  id: string;
  onClickedAddress: (value: ClickedAddress) => void;
  handleMouseEnter: (orderId: string, event: MouseEvent) => void;
  handleMouseLeave: () => void;
  currentRouteStatus?: RouteStatus | undefined;
  isLastItem?: boolean;
  locationSequence?: number;
  travelTimeFromStartingPoint?: number; // Travel time from starting point to first location
  // New props for line hover functionality
  onLineHover?: (
    sourceLocationId: string,
    destinationLocationId: string,
  ) => void;
  onLineHoverEnd?: () => void;
  hoveredLineSource?: string;
  hoveredLineDestination?: string;
  nextLocationId?: string; // ID of the next location in the sequence
  nextLocation?: AdminGetRoutesLocationsResDto; // The next location object with travel details
  actualServiceTime?: number; // Actual service time calculated from travelDetails (for live phase)
  adjustedServiceTime?: number; // Adjusted service time from real-time adjustments
  adjustedTravelTime?: number; // Adjusted travel time from real-time adjustments
  isUnassignedRoute?: boolean;
  isFirstLocationToRender?: boolean; // Whether this is the first location to render across all dropzones
  stackShift?: number; // Leftward px offset to fan out overlapping completed zero-service boxes
}

const DraggableOrder = ({
  order,
  routeId,
  regionId,
  color,
  selected,
  id,
  onClickedAddress,
  handleMouseEnter,
  handleMouseLeave,
  currentRouteStatus,
  isLastItem,
  locationSequence,
  travelTimeFromStartingPoint,
  onLineHover,
  onLineHoverEnd,
  hoveredLineSource,
  hoveredLineDestination,
  nextLocationId,
  nextLocation,
  actualServiceTime,
  adjustedServiceTime,
  adjustedTravelTime,
  isUnassignedRoute = false,
  isFirstLocationToRender = false,
  stackShift,
}: OrderItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: {
      routeId,
      regionId,
      id,
      type: "order",
    },
  });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      // Removed transition during drag to prevent interference
      transition: isDragging ? "none" : transition,
      opacity: isDragging ? 0.7 : 1,
      // Ensure the dragged element is above other elements
      zIndex: isDragging ? 9999 : "auto",
      // Improve drag visual feedback
      boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.2)" : "none",
      // Ensure pointer events work correctly during drag
      pointerEvents: (isDragging
        ? "none"
        : "auto") as React.CSSProperties["pointerEvents"],
    }),
    [transform, transition, isDragging],
  );

  // Determine hover states based on line hover
  const isSourceAddressDarkened = hoveredLineSource === id;
  const isDestinationAddressDarkened = hoveredLineDestination === id;
  const isLineDarkened =
    hoveredLineSource === id && hoveredLineDestination === nextLocationId;

  // Handle line hover events
  const handleLineMouseEnter = () => {
    if (onLineHover && nextLocationId) {
      onLineHover(id, nextLocationId);
    }
  };

  const handleLineMouseLeave = () => {
    if (onLineHoverEnd) {
      onLineHoverEnd();
    }
  };

  // Show travel time to the next location
  // We should show travel time from the last completed address to the first not completed address
  // Use adjusted travel time if available, otherwise use original
  const effectiveTravelTimeBetweenLocations =
    adjustedTravelTime ??
    nextLocation?.travelDetails?.travelTimeBetweenLocations;

  const effectiveServiceTime = getEffectiveServiceTime(
    order,
    actualServiceTime,
    adjustedServiceTime,
    currentRouteStatus,
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      // Lets the route planner scroll this order into view when it's selected
      data-location-id={id}
      {...attributes}
      {...listeners}
    >
      <LocationAddressComponent
        duration={order?.serviceTime || 5}
        locationSequence={isUnassignedRoute ? 0 : locationSequence || 0}
        color={color || "unassigned"}
        status={
          order?.driverStatus !== "toBePlowed"
            ? order?.driverStatus
            : order?.order?.status
        }
        selected={selected || false}
        last={isLastItem}
        first={isFirstLocationToRender}
        onAddressClick={(e) => {
          onClickedAddress({ e, address: id });
        }}
        onMouseEnter={!isDragging ? (e) => handleMouseEnter(id, e) : undefined}
        onMouseLeave={handleMouseLeave}
        hasDynamicWidth={true}
        serviceTime={effectiveServiceTime}
        travelTimeBetweenLocations={effectiveTravelTimeBetweenLocations}
        travelTimeFromStartingPoint={travelTimeFromStartingPoint}
        adjustedServiceTime={adjustedServiceTime}
        adjustedTravelTime={adjustedTravelTime}
        onLineMouseEnter={handleLineMouseEnter}
        onLineMouseLeave={handleLineMouseLeave}
        isSourceAddressDarkened={isSourceAddressDarkened}
        isDestinationAddressDarkened={isDestinationAddressDarkened}
        isLineDarkened={isLineDarkened}
        isUnassignedRoute={isUnassignedRoute}
        currentRouteStatus={currentRouteStatus}
        stackShift={stackShift}
      />
    </div>
  );
};

// Custom comparison function for memo - only re-render when meaningful props change
const arePropsEqual = (
  prevProps: OrderItemProps,
  nextProps: OrderItemProps,
): boolean => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.color === nextProps.color &&
    prevProps.locationSequence === nextProps.locationSequence &&
    prevProps.isLastItem === nextProps.isLastItem &&
    prevProps.isFirstLocationToRender === nextProps.isFirstLocationToRender &&
    prevProps.isUnassignedRoute === nextProps.isUnassignedRoute &&
    prevProps.currentRouteStatus === nextProps.currentRouteStatus &&
    prevProps.actualServiceTime === nextProps.actualServiceTime &&
    prevProps.adjustedServiceTime === nextProps.adjustedServiceTime &&
    prevProps.adjustedTravelTime === nextProps.adjustedTravelTime &&
    prevProps.stackShift === nextProps.stackShift &&
    prevProps.travelTimeFromStartingPoint ===
      nextProps.travelTimeFromStartingPoint &&
    prevProps.hoveredLineSource === nextProps.hoveredLineSource &&
    prevProps.hoveredLineDestination === nextProps.hoveredLineDestination &&
    prevProps.nextLocationId === nextProps.nextLocationId &&
    prevProps.order?.driverStatus === nextProps.order?.driverStatus &&
    prevProps.order?.driverStatusLastUpdatedBy ===
      nextProps.order?.driverStatusLastUpdatedBy &&
    prevProps.order?.order?.status === nextProps.order?.order?.status &&
    prevProps.order?.serviceTime === nextProps.order?.serviceTime &&
    prevProps.nextLocation?.travelDetails?.travelTimeBetweenLocations ===
      nextProps.nextLocation?.travelDetails?.travelTimeBetweenLocations
  );
};

export default memo(DraggableOrder, arePropsEqual);
